import React, { useState, useEffect, createContext, useContext, ReactNode } from "react";

export const BANNER_HEIGHT = 62; // px
export const BANNER_HEIGHT_MOBILE = 80; // px

const ANNOUNCEMENT_BANNER_COOKIE = "announcement_banner_dismissed";

// Context to share banner visibility state
interface BannerContextType {
  isBannerVisible: boolean;
  setBannerVisible: (visible: boolean) => void;
}

const BannerContext = createContext<BannerContextType | undefined>(undefined);

export const useBannerContext = () => {
  const context = useContext(BannerContext);
  if (!context) {
    throw new Error("useBannerContext must be used within BannerProvider");
  }
  return context;
};

export const BannerProvider = ({ children }: { children: ReactNode }) => {
  const [isBannerVisible, setBannerVisible] = useState(false);

  useEffect(() => {
    // Check if banner was previously dismissed
    const dismissed = document.cookie
      .split(";")
      .some((item) => item.trim().startsWith(`${ANNOUNCEMENT_BANNER_COOKIE}=true`));

    if (!dismissed) {
      setBannerVisible(true);
    }
  }, []);

  return (
    <BannerContext.Provider value={{ isBannerVisible, setBannerVisible }}>
      {children}
    </BannerContext.Provider>
  );
};

interface Props {
  message: string;
  linkText?: string;
  linkUrl?: string;
}

const AnnouncementBanner = ({ message, linkText, linkUrl }: Props) => {
  const { isBannerVisible, setBannerVisible } = useBannerContext();

  const handleClose = () => {
    // Set cookie to remember dismissal for 30 days
    const expiryDate = new Date();
    expiryDate.setTime(expiryDate.getTime() + 30 * 24 * 60 * 60 * 1000);
    document.cookie = `${ANNOUNCEMENT_BANNER_COOKIE}=true; expires=${expiryDate.toUTCString()}; path=/`;

    setBannerVisible(false);
  };

  if (!isBannerVisible) {
    return (
      <div
        className="w-full transition-[height] duration-[300ms] ease-[ease]"
        style={{ height: "0" }}
      />
    );
  }

  return (
    <>
      {/* BannerContainer */}
      <div
        className="fixed top-0 left-0 w-full p-4 flex items-center justify-between text-white z-[10002] max-[767.98px]:py-3 max-[767.98px]:px-4"
        style={{
          height: `${BANNER_HEIGHT}px`,
          background: "#24383d",
          fontFamily: "Inter, sans-serif",
          boxShadow: "0px 1px 0px rgba(0, 0, 0, 0.1)",
        }}
      >
        {/* BannerContent */}
        <div
          className="flex items-center justify-between w-full relative pr-2 pl-4 max-[767.98px]:pl-0 max-[767.98px]:pr-0"
          style={{ padding: "0 8px 0 16px" }}
        >
          {/* LeftContent */}
          <div className="flex items-center justify-center gap-4 flex-1 max-[767.98px]:flex-col max-[767.98px]:items-start max-[767.98px]:justify-start max-[767.98px]:gap-2 max-[767.98px]:min-w-0">
            {/* Combined Message and ButtonLink */}
            {linkText && linkUrl && (
              <a
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-[10px] text-white !no-underline flex-shrink-0 normal hover:opacity-90 py-2 px-3 -mx-3 -my-2 rounded"
                style={{
                  fontSize: "14px",
                  lineHeight: "1.2",
                  fontStyle: "normal",
                  fontFamily: "Inter",
                }}
              >
                {/* Message - normal weight */}
                <span className="font-normal whitespace-nowrap max-[767.98px]:whitespace-normal">
                  {message}
                </span>
                {/* Link Text - semibold */}
                <span className="font-semibold" style={{ letterSpacing: "0.21px" }}>
                  {linkText}
                </span>
                {/* ArrowIcon */}
                <svg
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-[18px] h-[18px] flex-shrink-0"
                >
                  <path
                    d="M9.88125 4.5L9 5.38125L12.3562 8.7375H4.5V10.0125H12.3562L9 13.3687L9.88125 14.25L14.625 9.375L9.88125 4.5Z"
                    fill="white"
                  />
                </svg>
              </a>
            )}
          </div>

          {/* CloseButton */}
          <button
            onClick={handleClose}
            aria-label="Close banner"
            className="bg-transparent border-none p-0 cursor-pointer flex items-center justify-center w-[30px] h-[30px] flex-shrink-0 opacity-80 transition-opacity duration-[200ms] hover:opacity-100"
          >
            {/* XMarkIcon */}
            <svg
              viewBox="0 0 30 30"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-[30px] h-[30px]"
            >
              <path
                d="M23.75 8.0125L21.9875 6.25L15 13.2375L8.0125 6.25L6.25 8.0125L13.2375 15L6.25 21.9875L8.0125 23.75L15 16.7625L21.9875 23.75L23.75 21.9875L16.7625 15L23.75 8.0125Z"
                fill="white"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* BannerPlaceholder */}
      <div
        className="w-full transition-[height] duration-[300ms] ease-[ease]"
        style={{
          height: isBannerVisible ? `${BANNER_HEIGHT}px` : "0",
        }}
      />

      {/* Mobile-specific height adjustments */}
      <style>{`
        @media (max-width: 767.98px) {
          .fixed.top-0.left-0 {
            height: ${BANNER_HEIGHT_MOBILE}px !important;
          }
          .w-full.transition-\\[height\\] {
            height: ${isBannerVisible ? `${BANNER_HEIGHT_MOBILE}px` : "0"} !important;
          }
        }
      `}</style>
    </>
  );
};

export default AnnouncementBanner;
